import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, switchMap, takeUntil, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { MeyMessageInboxClient, UserProfileDto, SendNewMessageRequest } from '../../../web-api-client';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-compose',
  templateUrl: './compose.component.html',
  styleUrls: ['./compose.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ComposeComponent implements OnInit, OnDestroy {
  @ViewChild('recipientInput') recipientInput: ElementRef<HTMLInputElement>;

  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();

  // Recipient search
  public searchTerm: string = '';
  public searchResults: UserProfileDto[] = [];
  public isSearching: boolean = false;
  public showDropdown: boolean = false;
  public selectedRecipients: UserProfileDto[] = [];
  public highlightedIndex: number = -1;

  // Form fields
  public subject: string = '';
  public body: string = '';
  public files: File[] = [];

  // State
  public isSending: boolean = false;

  constructor(
    private meyMessageInboxClient: MeyMessageInboxClient,
    private changeDetectorRef: ChangeDetectorRef,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Setup debounced search
    this.searchSubject.pipe(
      debounceTime(300),
      switchMap(term => {
        if (!term || term.length < 2) {
          return of({ profiles: [] });
        }
        this.isSearching = true;
        this.changeDetectorRef.detectChanges();
        return this.meyMessageInboxClient.searchUserProfiles(term, 20).pipe(
          catchError(err => {
            console.error('User profile search failed:', err);
            return of({ profiles: [] });
          })
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe(result => {
      this.searchResults = result.profiles || [];
      this.isSearching = false;
      this.showDropdown = this.searchResults.length > 0 || this.searchTerm.length >= 2;
      this.changeDetectorRef.detectChanges();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Recipient search
  onSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchTerm = input.value;
    this.highlightedIndex = -1;
    if (this.searchTerm.length >= 2) {
      this.searchSubject.next(this.searchTerm);
    } else {
      this.searchResults = [];
      this.showDropdown = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  // Keyboard navigation for dropdown
  onKeyDown(event: KeyboardEvent): void {
    if (!this.showDropdown && event.key !== 'Backspace') {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (this.highlightedIndex < this.searchResults.length - 1) {
          this.highlightedIndex++;
          this.changeDetectorRef.detectChanges();
        }
        break;

      case 'ArrowUp':
        event.preventDefault();
        if (this.highlightedIndex > 0) {
          this.highlightedIndex--;
          this.changeDetectorRef.detectChanges();
        }
        break;

      case 'Enter':
        event.preventDefault();
        if (this.highlightedIndex >= 0 && this.highlightedIndex < this.searchResults.length) {
          this.selectRecipient(this.searchResults[this.highlightedIndex]);
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.showDropdown = false;
        this.searchTerm = '';
        this.highlightedIndex = -1;
        this.changeDetectorRef.detectChanges();
        break;

      case 'Backspace':
        if (this.searchTerm === '' && this.selectedRecipients.length > 0) {
          // Remove last chip when backspace on empty input
          this.selectedRecipients.pop();
          this.changeDetectorRef.detectChanges();
        }
        break;
    }
  }

  // Check if profile is already selected
  isAlreadySelected(profile: UserProfileDto): boolean {
    return this.selectedRecipients.some(r => r.profileId === profile.profileId);
  }

  // Select recipient from dropdown
  selectRecipient(profile: UserProfileDto): void {
    if (!this.isAlreadySelected(profile)) {
      this.selectedRecipients.push(profile);
    }
    this.searchTerm = '';
    this.searchResults = [];
    this.showDropdown = false;
    this.highlightedIndex = -1;
    this.changeDetectorRef.detectChanges();
  }

  // Remove recipient chip
  removeRecipient(profile: UserProfileDto): void {
    this.selectedRecipients = this.selectedRecipients.filter(r => r.profileId !== profile.profileId);
    this.changeDetectorRef.detectChanges();
  }

  // Clear all recipients
  clearAllRecipients(): void {
    this.selectedRecipients = [];
    this.changeDetectorRef.detectChanges();
  }

  // Close dropdown when clicking outside
  onInputBlur(): void {
    // Small delay to allow click on dropdown item
    setTimeout(() => {
      this.showDropdown = false;
      this.changeDetectorRef.detectChanges();
    }, 200);
  }

  // File handling
  onSelect(event: any): void {
    this.files.push(...event.addedFiles);
    this.changeDetectorRef.detectChanges();
  }

  onRemove(file: File): void {
    this.files.splice(this.files.indexOf(file), 1);
    this.changeDetectorRef.detectChanges();
  }

  // Form validation
  isFormValid(): boolean {
    return this.selectedRecipients.length > 0 &&
           this.subject.trim().length > 0 &&
           this.body.trim().length > 0;
  }

  // Send message
  sendMessage(): void {
    if (!this.isFormValid()) {
      Swal.fire({
        icon: 'warning',
        title: 'Внимание',
        text: 'Моля, попълнете всички задължителни полета (получател, тема и съобщение).',
        confirmButtonText: 'OK'
      });
      return;
    }

    this.isSending = true;
    this.changeDetectorRef.detectChanges();

    const request = new SendNewMessageRequest({
      recipientProfileIds: this.selectedRecipients.map(r => r.profileId),
      subject: this.subject,
      body: this.body,
      attachmentBlobIds: [] // TODO: Handle file uploads if needed
    });

    this.meyMessageInboxClient.sendNewMessage(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.isSending = false;
          if (result.result) {
            Swal.fire({
              icon: 'success',
              title: 'Успех',
              text: 'Съобщението е изпратено успешно.',
              confirmButtonText: 'OK'
            }).then(() => {
              this.router.navigate(['/mail/outbox']);
            });
          } else {
            Swal.fire({
              icon: 'error',
              title: 'Грешка',
              text: result.errorMessage || 'Възникна грешка при изпращане на съобщението.',
              confirmButtonText: 'OK'
            });
          }
          this.changeDetectorRef.detectChanges();
        },
        error: (err) => {
          this.isSending = false;
          console.error('Send message failed:', err);
          Swal.fire({
            icon: 'error',
            title: 'Грешка',
            text: 'Възникна грешка при изпращане на съобщението.',
            confirmButtonText: 'OK'
          });
          this.changeDetectorRef.detectChanges();
        }
      });
  }

  // Cancel and go back
  cancel(): void {
    this.router.navigate(['/mail/inbox']);
  }
}
